import { Injectable } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { GamedataDownloadDialogComponent } from '../components/gamedata-download-dialog/gamedata-download-dialog.component';

/**
 * Service for handling gamedata SQLite file downloads
 * Provides utilities for building URLs, filenames, and triggering downloads
 */
@Injectable({
  providedIn: 'root'
})
export class GamedataDownloadService {
  // CloudFront base URL for gamedata downloads
  private readonly GAMEDATA_CDN_BASE_URL = 'https://d1c8ke6wmkdt0e.cloudfront.net/game_data';

  constructor(private dialogService: DialogService) {}

  /**
   * Build the download URL for a gamedata SQLite file
   * @param buildVersion - The build version (e.g., "abc123def456")
   * @param isLite - Whether to get the lite version (default: false)
   * @returns The full CloudFront URL
   */
  buildDownloadUrl(buildVersion: string, isLite: boolean = false): string {
    const versionSuffix = isLite ? '_lite' : '';
    return `${this.GAMEDATA_CDN_BASE_URL}/${buildVersion}${versionSuffix}`;
  }

  /**
   * Build the filename for a downloaded gamedata SQLite file
   * @param buildVersion - The build version
   * @param environment - The environment (e.g., "dev", "qa", "stage")
   * @param isLite - Whether this is the lite version (default: false)
   * @returns The formatted filename
   */
  buildFilename(buildVersion: string, environment: string, isLite: boolean = false): string {
    const dbType = isLite ? 'lite' : 'full';
    return `gamedata-${dbType}-${buildVersion}-${environment}.sqlite`;
  }

  /**
   * Trigger a file download via temporary anchor element
   * @param url - The download URL
   * @param filename - The filename to save as
   */
  triggerDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Prompt user to choose between full and lite gamedata using PrimeNG dialog
   * @param buildVersion - The build version to display in the prompt
   * @param environment - The environment to display in the prompt
   * @returns Promise that resolves to 'full', 'lite', or null if cancelled
   */
  async promptForDatabaseType(buildVersion: string, environment?: string): Promise<'full' | 'lite' | null> {
    return new Promise((resolve) => {
      const ref = this.dialogService.open(GamedataDownloadDialogComponent, {
        header: 'Choose SQLite Database to Download',
        width: '650px',
        modal: true,
        dismissableMask: true,
        styleClass: 'gamedata-download-dialog-modal',
        data: {
          buildVersion,
          environment
        }
      });

      ref.onClose.subscribe((result: 'full' | 'lite' | null) => {
        resolve(result || null);
      });
    });
  }

  /**
   * Download a gamedata SQLite file with user prompt
   * Combines all helper methods for a complete download flow
   * @param buildVersion - The build version to download
   * @param environment - The environment (for filename)
   * @returns Promise that resolves to the type downloaded ('full' or 'lite'), or null if cancelled/no buildVersion
   */
  async downloadWithPrompt(buildVersion: string | null, environment: string): Promise<'full' | 'lite' | null> {
    if (!buildVersion) {
      console.warn('⚠️ No build version provided for download');
      alert('No build version available for download.');
      return null;
    }

    try {
      // Prompt user for full vs lite using PrimeNG dialog
      const dbType = await this.promptForDatabaseType(buildVersion, environment);
      
      if (!dbType) {
        // User cancelled the dialog
        return null;
      }

      const isLite = dbType === 'lite';

      // Build URL and filename
      const downloadUrl = this.buildDownloadUrl(buildVersion, isLite);
      const filename = this.buildFilename(buildVersion, environment, isLite);

      // Trigger download
      this.triggerDownload(downloadUrl, filename);

      console.log(`✅ Downloading ${dbType} SQLite database:`, filename, 'from:', downloadUrl);
      
      return dbType;
    } catch (error) {
      console.error('❌ Error downloading SQLite file:', error);
      alert('Failed to download SQLite file. Please check the console for details.');
      return null;
    }
  }

  /**
   * Get the base CDN URL (useful for debugging or custom implementations)
   */
  getCdnBaseUrl(): string {
    return this.GAMEDATA_CDN_BASE_URL;
  }
}

